import * as ReviewCriteriaDAO from 'notebook/reviews/ReviewCriteriaDAO';
import notifications, {Response} from 'notifications';
import {BenchlingDispatch, Thunk} from 'redux-stores/Store';

import {
  ReviewCriteriaGroup,
  ReviewCriterionStatus,
  SavedReviewCriteriaGroup,
  SavedReviewCriterion,
} from './ReviewCriteriaConstants';

export enum ReviewCriteriaActionType {
  REVIEW_CRITERIA_FETCH_COMPLETED = 'REVIEW_CRITERIA_FETCH_COMPLETED',
  REVIEW_CRITERIA_ADD_GROUP = 'REVIEW_CRITERIA_ADD_GROUP',
  REVIEW_CRITERIA_GROUP_START_EDIT = 'REVIEW_CRITERIA_GROUP_START_EDIT',
  REVIEW_CRITERIA_GROUP_CHANGE_NAME = 'REVIEW_CRITERIA_GROUP_CHANGE_NAME',
  REVIEW_CRITERIA_GROUP_CANCEL_EDIT = 'REVIEW_CRITERIA_GROUP_CANCEL_EDIT',
  REVIEW_CRITERIA_GROUP_ADD_CRITERION = 'REVIEW_CRITERIA_GROUP_ADD_CRITERION',
  REVIEW_CRITERIA_GROUP_CHANGE_CRITERION = 'REVIEW_CRITERIA_GROUP_CHANGE_CRITERION',
  REVIEW_CRITERIA_GROUP_DELETE_CRITERION = 'REVIEW_CRITERIA_GROUP_DELETE_CRITERION',
  REVIEW_CRITERIA_GROUP_START_CREATE = 'REVIEW_CRITERIA_GROUP_START_CREATE',
  REVIEW_CRITERIA_GROUP_CREATE_FAILED = 'REVIEW_CRITERIA_GROUP_CREATE_FAILED',
  REVIEW_CRITERIA_GROUP_CREATE_COMPLETED = 'REVIEW_CRITERIA_GROUP_CREATE_COMPLETED',
  REVIEW_CRITERIA_GROUP_START_UPDATE = 'REVIEW_CRITERIA_GROUP_START_UPDATE',
  REVIEW_CRITERIA_GROUP_UPDATE_FAILED = 'REVIEW_CRITERIA_GROUP_UPDATE_FAILED',
  REVIEW_CRITERIA_GROUP_UPDATE_COMPLETED = 'REVIEW_CRITERIA_GROUP_UPDATE_COMPLETED',
  REVIEW_CRITERIA_GROUP_START_DELETE = 'REVIEW_CRITERIA_GROUP_START_DELETE',
  REVIEW_CRITERIA_GROUP_DELETE_FAILED = 'REVIEW_CRITERIA_GROUP_DELETE_FAILED',
  REVIEW_CRITERIA_GROUP_DELETE_COMPLETED = 'REVIEW_CRITERIA_GROUP_DELETE_COMPLETED',
  REVIEW_CRITERIA_NEW_GROUP_SCROLLED_INTO_VIEW = 'REVIEW_CRITERIA_NEW_GROUP_SCROLLED_INTO_VIEW',
  REVIEW_CRITERION_UPDATED = 'REVIEW_CRITERION_UPDATED',
}

interface ReviewCriteriaFetchCompletedAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_FETCH_COMPLETED;
  linkedItemId: string;
  reviewCriteriaGroups: Array<SavedReviewCriteriaGroup>;
}

export function fetchCompleted(
  linkedItemId: string,
  reviewCriteriaGroups: Array<SavedReviewCriteriaGroup>
): ReviewCriteriaFetchCompletedAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_FETCH_COMPLETED,
    linkedItemId,
    reviewCriteriaGroups,
  };
}

export function fetchReviewCriteria(linkedItemId: string): Thunk<void> {
  return (dispatch: BenchlingDispatch) =>
    ReviewCriteriaDAO.fetchReviewCriteriaGroups(linkedItemId).then(
      ({data: reviewCriteriaGroups}: {data: Array<SavedReviewCriteriaGroup>}) => {
        dispatch(fetchCompleted(linkedItemId, reviewCriteriaGroups));
      },
      notifications.notifyError
    );
}

interface ReviewCriteriaAddGroupAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_ADD_GROUP;
  linkedItemId: string;
}

export function addGroup(linkedItemId: string): ReviewCriteriaAddGroupAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_ADD_GROUP,
    linkedItemId,
  };
}

interface ReviewCriteriaGroupStartEditAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_EDIT;
  linkedItemId: string;
  reviewCriteriaGroupId: string;
  reviewCriteriaGroup: ReviewCriteriaGroup;
}

export function startEdit(
  linkedItemId: string,
  reviewCriteriaGroupId: string,
  reviewCriteriaGroup: ReviewCriteriaGroup
): ReviewCriteriaGroupStartEditAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_EDIT,
    linkedItemId,
    // Redundant, but needed to route the action to the correct part of the state
    reviewCriteriaGroupId,
    reviewCriteriaGroup,
  };
}

interface ReviewCriteriaGroupChangeNameAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CHANGE_NAME;
  linkedItemId: string;
  reviewCriteriaGroupId: string;
  name: string;
}

export function changeName(
  linkedItemId: string,
  reviewCriteriaGroupId: string,
  name: string
): ReviewCriteriaGroupChangeNameAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CHANGE_NAME,
    linkedItemId,
    reviewCriteriaGroupId,
    name,
  };
}

interface ReviewCriteriaGroupCancelEditAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CANCEL_EDIT;
  linkedItemId: string;
  reviewCriteriaGroupId: string;
}

export function cancelEdit(
  linkedItemId: string,
  reviewCriteriaGroupId: string
): ReviewCriteriaGroupCancelEditAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CANCEL_EDIT,
    linkedItemId,
    reviewCriteriaGroupId,
  };
}

interface ReviewCriteriaGroupAddCriterionAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_ADD_CRITERION;
  linkedItemId: string;
  reviewCriteriaGroupId: string;
}

export function addCriterion(
  linkedItemId: string,
  reviewCriteriaGroupId: string
): ReviewCriteriaGroupAddCriterionAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_ADD_CRITERION,
    linkedItemId,
    reviewCriteriaGroupId,
  };
}

interface ReviewCriteriaGroupChangeCriterionAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CHANGE_CRITERION;
  linkedItemId: string;
  reviewCriteriaGroupId: string;
  rowIndex: number;
  text: string;
}

export function changeCriterion(
  linkedItemId: string,
  reviewCriteriaGroupId: string,
  rowIndex: number,
  text: string
): ReviewCriteriaGroupChangeCriterionAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CHANGE_CRITERION,
    linkedItemId,
    reviewCriteriaGroupId,
    rowIndex,
    text,
  };
}

interface ReviewCriteriaGroupDeleteCriterionAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_DELETE_CRITERION;
  linkedItemId: string;
  reviewCriteriaGroupId: string;
  rowIndex: number;
}

export function deleteCriterion(
  linkedItemId: string,
  reviewCriteriaGroupId: string,
  rowIndex: number
): ReviewCriteriaGroupDeleteCriterionAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_DELETE_CRITERION,
    linkedItemId,
    reviewCriteriaGroupId,
    rowIndex,
  };
}

interface ReviewCriteriaGroupStartCreateAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_CREATE;
  linkedItemId: string;
}

function startCreate(linkedItemId: string): ReviewCriteriaGroupStartCreateAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_CREATE,
    linkedItemId,
  };
}

interface ReviewCriteriaGroupCreateFailedAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CREATE_FAILED;
  linkedItemId: string;
}
function createFailed(linkedItemId: string): ReviewCriteriaGroupCreateFailedAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CREATE_FAILED,
    linkedItemId,
  };
}

interface ReviewCriteriaGroupCreateCompletedAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CREATE_COMPLETED;
  linkedItemId: string;
  savedReviewCriteriaGroup: SavedReviewCriteriaGroup;
}

export function groupCreated(
  linkedItemId: string,
  savedReviewCriteriaGroup: SavedReviewCriteriaGroup
): ReviewCriteriaGroupCreateCompletedAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_CREATE_COMPLETED,
    linkedItemId,
    savedReviewCriteriaGroup,
  };
}

export function createReviewCriteriaGroup(
  linkedItemId: string,
  reviewCriteriaGroup: ReviewCriteriaGroup
): Thunk<void> {
  return (dispatch: BenchlingDispatch) => {
    dispatch(startCreate(linkedItemId));
    ReviewCriteriaDAO.createReviewCriteriaGroup(
      linkedItemId,
      reviewCriteriaGroup.name,
      reviewCriteriaGroup.reviewCriteria
    ).then(
      (savedReviewCriteriaGroup: SavedReviewCriteriaGroup) => {
        dispatch(groupCreated(linkedItemId, savedReviewCriteriaGroup));
        notifications.success('Successfully created review criteria.');
      },
      (errorResp: Response) => {
        dispatch(createFailed(linkedItemId));
        notifications.notifyError(errorResp);
      }
    );
  };
}

interface ReviewCriteriaGroupStartUpdateAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_UPDATE;
  linkedItemId: string;
  reviewCriteriaGroupId: string;
}

function startUpdate(
  linkedItemId: string,
  reviewCriteriaGroupId: string
): ReviewCriteriaGroupStartUpdateAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_UPDATE,
    linkedItemId,
    reviewCriteriaGroupId,
  };
}

interface ReviewCriteriaGroupUpdateFailedAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_UPDATE_FAILED;
  linkedItemId: string;
  reviewCriteriaGroupId: string;
}

function groupUpdateFailed(
  linkedItemId: string,
  reviewCriteriaGroupId: string
): ReviewCriteriaGroupUpdateFailedAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_UPDATE_FAILED,
    linkedItemId,
    reviewCriteriaGroupId,
  };
}

interface ReviewCriteriaGroupUpdateCompletedAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_UPDATE_COMPLETED;
  linkedItemId: string;
  reviewCriteriaGroupId: string;
  savedReviewCriteriaGroup: SavedReviewCriteriaGroup;
}

export function groupUpdated(
  linkedItemId: string,
  savedReviewCriteriaGroup: SavedReviewCriteriaGroup
): ReviewCriteriaGroupUpdateCompletedAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_UPDATE_COMPLETED,
    linkedItemId,
    reviewCriteriaGroupId: savedReviewCriteriaGroup.id,
    savedReviewCriteriaGroup,
  };
}

export function updateReviewCriteriaGroup(
  linkedItemId: string,
  reviewCriteriaGroup: SavedReviewCriteriaGroup
): Thunk<void> {
  return (dispatch) => {
    dispatch(startUpdate(linkedItemId, reviewCriteriaGroup.id));
    ReviewCriteriaDAO.updateReviewCriteriaGroup(
      reviewCriteriaGroup.id,
      reviewCriteriaGroup.name,
      reviewCriteriaGroup.reviewCriteria
    ).then(
      (savedReviewCriteriaGroup: SavedReviewCriteriaGroup) => {
        dispatch(groupUpdated(linkedItemId, savedReviewCriteriaGroup));
        notifications.success('Successfully updated review criteria.');
      },
      (errorResp: Response) => {
        dispatch(groupUpdateFailed(linkedItemId, reviewCriteriaGroup.id));
        notifications.notifyError(errorResp);
      }
    );
  };
}

interface ReviewCriteriaGroupStartDeleteAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_DELETE;
  reviewCriteriaGroupId: string;
}
function startDelete(reviewCriteriaGroupId: string): ReviewCriteriaGroupStartDeleteAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_START_DELETE,
    reviewCriteriaGroupId,
  };
}

interface ReviewCriteriaGroupDeleteFailedAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_DELETE_FAILED;
  reviewCriteriaGroupId: string;
}
function groupDeleteFailed(reviewCriteriaGroupId: string): ReviewCriteriaGroupDeleteFailedAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_DELETE_FAILED,
    reviewCriteriaGroupId,
  };
}

interface ReviewCriteriaGroupDeleteCompletedAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_DELETE_COMPLETED;
  linkedItemId: string;
  reviewCriteriaGroupId: string;
}
export function groupDeleted(
  linkedItemId: string,
  reviewCriteriaGroupId: string
): ReviewCriteriaGroupDeleteCompletedAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_GROUP_DELETE_COMPLETED,
    linkedItemId,
    reviewCriteriaGroupId,
  };
}

export function deleteReviewCriteriaGroup(linkedItemId: string, reviewCriteriaGroupId: string): Thunk<void> {
  return (dispatch: BenchlingDispatch) => {
    dispatch(startDelete(reviewCriteriaGroupId));
    ReviewCriteriaDAO.deleteReviewCriteriaGroup(reviewCriteriaGroupId).then(
      () => {
        dispatch(groupDeleted(linkedItemId, reviewCriteriaGroupId));
        notifications.success('Successfully deleted review criteria.');
      },
      (errorResp: Response) => {
        dispatch(groupDeleteFailed(reviewCriteriaGroupId));
        notifications.notifyError(errorResp);
      }
    );
  };
}

interface ReviewCriteriaNewGroupScrolledIntoViewAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERIA_NEW_GROUP_SCROLLED_INTO_VIEW;
  linkedItemId: string;
}

export function newGroupScrolledIntoView(linkedItemId: string): ReviewCriteriaNewGroupScrolledIntoViewAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERIA_NEW_GROUP_SCROLLED_INTO_VIEW,
    linkedItemId,
  };
}

interface ReviewCriterionUpdatedAction {
  type: ReviewCriteriaActionType.REVIEW_CRITERION_UPDATED;
  criterionId: string;
  criterion: SavedReviewCriterion;
}

export function reviewCriterionUpdated(
  criterionId: string,
  criterion: SavedReviewCriterion
): ReviewCriterionUpdatedAction {
  return {
    type: ReviewCriteriaActionType.REVIEW_CRITERION_UPDATED,
    criterionId,
    criterion,
  };
}

export function updateReviewCriterion(
  criterionId: string,
  {status}: {status: ReviewCriterionStatus}
): Thunk<void> {
  return async (dispatch: BenchlingDispatch) => {
    const {criterion} = await ReviewCriteriaDAO.updateReviewCriterion(criterionId, {status});
    dispatch(reviewCriterionUpdated(criterionId, criterion));
  };
}

export type ReviewCriteriaAction =
  | ReviewCriteriaFetchCompletedAction
  | ReviewCriteriaAddGroupAction
  | ReviewCriteriaGroupStartEditAction
  | ReviewCriteriaGroupChangeNameAction
  | ReviewCriteriaGroupCancelEditAction
  | ReviewCriteriaGroupAddCriterionAction
  | ReviewCriteriaGroupChangeCriterionAction
  | ReviewCriteriaGroupDeleteCriterionAction
  | ReviewCriteriaGroupStartCreateAction
  | ReviewCriteriaGroupCreateCompletedAction
  | ReviewCriteriaGroupCreateFailedAction
  | ReviewCriteriaGroupStartUpdateAction
  | ReviewCriteriaGroupUpdateFailedAction
  | ReviewCriteriaGroupUpdateCompletedAction
  | ReviewCriteriaGroupStartDeleteAction
  | ReviewCriteriaGroupDeleteFailedAction
  | ReviewCriteriaGroupDeleteCompletedAction
  | ReviewCriteriaNewGroupScrolledIntoViewAction
  | ReviewCriterionUpdatedAction;
